import { updateId } from 'expo-updates';
import { Platform } from 'react-native';

export const APP_UPDATE_TYPE_OTA = 'ota';
export const APP_UPDATE_TYPE_EMBEDDED = 'embedded';

export type AppUpdateType =
  typeof APP_UPDATE_TYPE_OTA | typeof APP_UPDATE_TYPE_EMBEDDED;

export const APP_UPDATE_TYPE_PROPERTY = 'app_update_type';
export const APP_UPDATE_ID_PROPERTY = 'app_update_id';
export const DOWNLOADED_UPDATE_ID_PROPERTY = 'downloaded_update_id';

// A non-null expo-updates updateId is the authoritative signal that the running JS
// bundle came from an over-the-air update rather than the embedded native build. On
// web and on embedded launches updateId is null, so the type falls back to embedded.
export const getExpoUpdateId = (): string | undefined => updateId ?? undefined;

export const SHORT_EXPO_UPDATE_ID_LENGTH = 8;

// Short, git-SHA-style identifier of the running OTA update for user-facing display.
// Returns undefined on embedded launches so callers can omit the line entirely.
export const getShortExpoUpdateId = (): string | undefined =>
  updateId ? updateId.slice(0, SHORT_EXPO_UPDATE_ID_LENGTH) : undefined;

export const getAppUpdateType = (): AppUpdateType =>
  updateId ? APP_UPDATE_TYPE_OTA : APP_UPDATE_TYPE_EMBEDDED;

// Analytics properties describing the running bundle: the OTA-vs-embedded type plus the
// exact updateId when an OTA is running, so events can be pinned to a specific update in
// Amplitude/EAS. OTA is a native-only mechanism (expo-updates), so these properties are
// omitted on web to avoid mislabeling web traffic as embedded native builds in
// cross-platform OTA reports.
export const getAppUpdateProperties = (): Record<string, string> => {
  if (Platform.OS === 'web') {
    return {};
  }

  const activeUpdateId = getExpoUpdateId();

  return {
    [APP_UPDATE_TYPE_PROPERTY]: getAppUpdateType(),
    ...(activeUpdateId ? { [APP_UPDATE_ID_PROPERTY]: activeUpdateId } : {}),
  };
};
